/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 *
 * Resilient wrapper around get-categories-hierarchy: on API/backend failure returns []
 * so navigation and link hydration do not crash the page.
 */
/// <reference path="../typings/retail-actions-subpaths.d.ts" />

import {
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext,
    IGeneric,
    IAny
} from '@msdyn365-commerce/core';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import {
    createCategoriesHierarchyInput,
    getCategoryHierarchyAction
} from '@msdyn365-commerce-modules/retail-actions/dist/lib/get-categories-hierarchy';

/**
 * Safe wrapper: call the real action, and fallback to [] on failure.
 */
export async function getCategoryHierarchySafeAction(input: IActionInput, ctx: IActionContext): Promise<CategoryHierarchy[]> {
    try {
        return await getCategoryHierarchyAction(input, ctx);
    } catch (error) {
        ctx.telemetry?.warning?.(
            `[get-categories-hierarchy-safe] Backend call failed; returning empty categories. Error: ${
                error instanceof Error ? error.message : String(error)
            }`
        );
        return [];
    }
}

export const getCategoryHierarchySafeDataAction = createObservableDataAction({
    id: 'get-categories-hierarchy-safe',
    action: getCategoryHierarchySafeAction as IAction<CategoryHierarchy[]>,
    input: createCategoriesHierarchyInput as (inputData: ICreateActionContext<IGeneric<IAny>>) => IActionInput
});

export default getCategoryHierarchySafeDataAction;
